import { useMemo, useCallback } from 'react';
import { useTracking } from 'react-tracking';
import type { HTMLReactParserOptions, DOMNode } from 'html-react-parser';
import parse, { domToReact, Element } from 'html-react-parser';
import UniversalLink from '@/components/UniversalLink';
import { Feedback } from '../components/styled';
import loadable from '@loadable/component';
import useUser from '@/hooks/useUser';

const RegisterButton = loadable(() => import('../components/RegisterButton'), {
  ssr: false,
});
export const useBodyParser = ({ body }: { body: string }) => {
  const { trackEvent } = useTracking();
  const { data: userData } = useUser();

  const handleClick = useCallback(
    (e) => {
      trackEvent({
        type: 'track',
        action: 'click',
        subject: 'article_body_link',
        href: e.currentTarget.href,
      });
    },
    [trackEvent]
  );

  const parsedBody = useMemo(() => {
    const parseOptions: HTMLReactParserOptions = {
      replace: (domNode: DOMNode) => {
        if (domNode instanceof Element && domNode.name === 'font') {
          return <>{domToReact(domNode.children, parseOptions)}</>;
        }

        if (domNode instanceof Element && domNode.name === 'figure') {
          return <figure>{domToReact(domNode.children, parseOptions)}</figure>;
        }

        if (
          domNode instanceof Element &&
          domNode.name === 'a' &&
          domNode.attribs.href
        ) {
          /** check if link is mailto */
          if (domNode.attribs.href?.includes('mailto:')) {
            return (
              <a href={domNode.attribs.href} rel={'nofollow'}>
                {domToReact(domNode.children, parseOptions)}
              </a>
            );
          }
          return (
            <UniversalLink to={domNode.attribs.href} onClick={handleClick}>
              {domToReact(domNode.children, parseOptions)}
            </UniversalLink>
          );
        }

        if (
          domNode instanceof Element &&
          domNode.name === 'p' &&
          domNode.children
        ) {
          const isFeedback = domNode.children.some(
            (child) =>
              child instanceof Element &&
              child.name === 'strong' &&
              child.children &&
              child.children.some(
                (grandChild) =>
                  grandChild['data'] &&
                  grandChild['data'].includes('Have feedback on this article?')
              )
          );

          if (isFeedback) {
            return (
              <>
                {userData.loggedIn ? null : <RegisterButton />}

                <Feedback>
                  {domToReact(domNode.children, parseOptions)}
                </Feedback>
              </>
            );
          }
        }
      },
    };
    return parse(body, parseOptions);
  }, [body, userData]);
  return parsedBody;
};
